import { useState, useEffect } from 'react'
import { LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { DollarSign, TrendingUp, TrendingDown, PieChart as PieChartIcon, Calculator, Target, Calendar, ShoppingCart, Package } from 'lucide-react'
import { generateFinanceData, CROP_CONFIG } from '../../utils/mockDataGenerator'
import './FinanceDashboard.css'

function FinanceDashboard({ crop }) {
    const [financeData, setFinanceData] = useState(null)
    const [activeTab, setActiveTab] = useState('overview')

    useEffect(() => {
        const data = generateFinanceData(crop)
        setFinanceData(data)
    }, [crop])

    if (!financeData) return <div className="loading">Cargando datos financieros...</div>

    const { period, sales, costs, cashFlow, summary, kpis } = financeData
    const cropConfig = CROP_CONFIG[crop] || CROP_CONFIG['Tomate']

    const formatCurrency = (value) => {
        if (value >= 1000000) {
            return `$${(value / 1000000).toFixed(1)}M`
        }
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            maximumFractionDigits: 0
        }).format(value)
    }

    const formatFullCurrency = (value) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            maximumFractionDigits: 0
        }).format(value)
    }

    const COLORS = ['#10B981', '#0EA5E9', '#F59E0B', '#8B5CF6', '#EC4899', '#6366F1', '#14B8A6']

    // Prepare costs for pie chart
    const costsForPie = costs.map((c, i) => ({
        ...c,
        fill: COLORS[i % COLORS.length]
    }))

    // Daily sales chart data (last 15 sales)
    const salesChartData = sales.slice(-15).map(s => ({
        date: s.dateDisplay,
        total: s.total,
        kg: s.quantityKg
    }))

    return (
        <div className="finance-dashboard">
            <div className="dashboard-header">
                <div>
                    <h2>💰 Inteligencia Financiera - {crop}</h2>
                    <p>
                        <Calendar size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                        Período: <strong>{period.label}</strong>
                    </p>
                </div>
                <div className="tab-buttons">
                    <button
                        className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        Resumen
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'sales' ? 'active' : ''}`}
                        onClick={() => setActiveTab('sales')}
                    >
                        Ventas
                    </button>
                    <button
                        className={`tab-btn ${activeTab === 'costs' ? 'active' : ''}`}
                        onClick={() => setActiveTab('costs')}
                    >
                        Costos
                    </button>
                </div>
            </div>

            {/* KPIs Row */}
            <div className="kpis-grid">
                <div className="kpi-card glass-card positive">
                    <div className="kpi-icon"><TrendingUp size={24} /></div>
                    <div className="kpi-content">
                        <span className="kpi-value">{formatCurrency(summary.totalIncome)}</span>
                        <span className="kpi-label">Ingresos Totales</span>
                    </div>
                    <span className="kpi-badge">{summary.totalKgSold} kg vendidos</span>
                </div>
                <div className="kpi-card glass-card negative">
                    <div className="kpi-icon"><TrendingDown size={24} /></div>
                    <div className="kpi-content">
                        <span className="kpi-value">{formatCurrency(summary.totalExpenses)}</span>
                        <span className="kpi-label">Egresos Totales</span>
                    </div>
                </div>
                <div className="kpi-card glass-card">
                    <div className="kpi-icon"><PieChartIcon size={24} /></div>
                    <div className="kpi-content">
                        <span className="kpi-value">{summary.grossMargin}%</span>
                        <span className="kpi-label">Margen Bruto</span>
                    </div>
                </div>
                <div className="kpi-card glass-card highlight">
                    <div className="kpi-icon"><Calculator size={24} /></div>
                    <div className="kpi-content">
                        <span className="kpi-value">{summary.roi}%</span>
                        <span className="kpi-label">Retorno (ROI)</span>
                    </div>
                </div>
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
                <>
                    <div className="charts-grid">
                        <div className="chart-card glass-card large">
                            <h3>📈 Flujo de Caja (Últimos 6 meses)</h3>
                            <ResponsiveContainer width="100%" height={280}>
                                <AreaChart data={cashFlow}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                    <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                                    <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${(v / 1000000).toFixed(0)}M`} />
                                    <Tooltip
                                        contentStyle={{ background: '#1E293B', border: '1px solid #334155', borderRadius: '8px' }}
                                        formatter={(v) => formatFullCurrency(v)}
                                    />
                                    <Legend />
                                    <Area type="monotone" dataKey="income" fill="rgba(16, 185, 129, 0.2)" stroke="#10B981" name="Ingresos" />
                                    <Area type="monotone" dataKey="expenses" fill="rgba(239, 68, 68, 0.2)" stroke="#EF4444" name="Egresos" />
                                    <Line type="monotone" dataKey="profit" stroke="#0EA5E9" strokeWidth={2} name="Utilidad" dot={false} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="chart-card glass-card">
                            <h3>📊 Distribución de Costos</h3>
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie
                                        data={costsForPie}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={2}
                                        dataKey="amount"
                                        nameKey="category"
                                        label={({ category, percentage }) => `${percentage}%`}
                                    >
                                        {costsForPie.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.fill} />
                                        ))}
                                    </Pie>
                                    <Tooltip
                                        contentStyle={{ background: '#1E293B', border: '1px solid #334155', borderRadius: '8px' }}
                                        formatter={(v) => formatFullCurrency(v)}
                                    />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* KPIs Summary */}
                    <div className="summary-section glass-card">
                        <h3><Target size={20} /> Indicadores Clave de {crop}</h3>
                        <div className="indicators-grid">
                            <div className="indicator">
                                <span className="indicator-label">Punto de Equilibrio</span>
                                <span className="indicator-value">{kpis.breakEvenKg.toLocaleString()} kg/mes</span>
                            </div>
                            <div className="indicator">
                                <span className="indicator-label">Utilidad Bruta</span>
                                <span className="indicator-value">{formatCurrency(summary.grossProfit)}</span>
                            </div>
                            <div className="indicator">
                                <span className="indicator-label">Precio Promedio</span>
                                <span className="indicator-value">${summary.avgPricePerKg.toLocaleString()}/kg</span>
                            </div>
                            <div className="indicator">
                                <span className="indicator-label">Costo por kg</span>
                                <span className="indicator-value">${kpis.costPerKg.toLocaleString()}/kg</span>
                            </div>
                            <div className="indicator">
                                <span className="indicator-label">Utilidad por kg</span>
                                <span className="indicator-value positive">${kpis.profitPerKg.toLocaleString()}/kg</span>
                            </div>
                            <div className="indicator">
                                <span className="indicator-label">Precio Referencia</span>
                                <span className="indicator-value">${cropConfig.finance.pricePerKg.toLocaleString()}/kg</span>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Sales Tab */}
            {activeTab === 'sales' && (
                <>
                    <div className="charts-grid">
                        <div className="chart-card glass-card large">
                            <h3><ShoppingCart size={18} /> Ventas Diarias ({period.label})</h3>
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={salesChartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                    <XAxis dataKey="date" stroke="#64748B" fontSize={11} />
                                    <YAxis stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
                                    <Tooltip
                                        contentStyle={{ background: '#1E293B', border: '1px solid #334155', borderRadius: '8px' }}
                                        formatter={(v, name) => [name === 'total' ? formatFullCurrency(v) : `${v} kg`, name === 'total' ? 'Venta' : 'Cantidad']}
                                    />
                                    <Bar dataKey="total" fill="#10B981" name="total" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    <div className="table-section glass-card">
                        <h3><Package size={18} /> Detalle de Ventas (Últimas 10)</h3>
                        <div className="table-wrapper">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Fecha</th>
                                        <th>Cliente</th>
                                        <th>Cantidad</th>
                                        <th>Precio/kg</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sales.slice(-10).reverse().map(sale => (
                                        <tr key={sale.id}>
                                            <td>{sale.dateDisplay}</td>
                                            <td>{sale.client}</td>
                                            <td>{sale.quantityKg.toLocaleString()} kg</td>
                                            <td>${sale.pricePerKg.toLocaleString()}</td>
                                            <td className="positive">{formatFullCurrency(sale.total)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            {/* Costs Tab */}
            {activeTab === 'costs' && (
                <>
                    <div className="charts-grid">
                        <div className="chart-card glass-card large">
                            <h3>📉 Costos por Categoría</h3>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={costs} layout="vertical">
                                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                    <XAxis type="number" stroke="#64748B" fontSize={12} tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
                                    <YAxis dataKey="category" type="category" stroke="#64748B" fontSize={11} width={120} />
                                    <Tooltip
                                        contentStyle={{ background: '#1E293B', border: '1px solid #334155', borderRadius: '8px' }}
                                        formatter={(v) => formatFullCurrency(v)}
                                    />
                                    <Bar dataKey="amount" name="Monto" radius={[0, 4, 4, 0]}>
                                        {costs.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.type === 'Variable' ? '#F59E0B' : '#0EA5E9'} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>

                        <div className="chart-card glass-card">
                            <h3>📋 Resumen de Costos</h3>
                            <div className="costs-summary">
                                <div className="cost-type-card">
                                    <div className="cost-type-header variable">
                                        <span className="cost-dot"></span>
                                        Costos Variables
                                    </div>
                                    <div className="cost-type-value">
                                        {formatCurrency(costs.filter(c => c.type === 'Variable').reduce((sum, c) => sum + c.amount, 0))}
                                    </div>
                                </div>
                                <div className="cost-type-card">
                                    <div className="cost-type-header fixed">
                                        <span className="cost-dot"></span>
                                        Costos Fijos
                                    </div>
                                    <div className="cost-type-value">
                                        {formatCurrency(costs.filter(c => c.type === 'Fijo').reduce((sum, c) => sum + c.amount, 0))}
                                    </div>
                                </div>
                                <div className="costs-list">
                                    {costs.map(cost => (
                                        <div key={cost.id} className="cost-item">
                                            <span className="cost-name">{cost.category}</span>
                                            <span className={`cost-badge ${cost.type.toLowerCase()}`}>{cost.type}</span>
                                            <span className="cost-amount">{formatCurrency(cost.amount)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

export default FinanceDashboard
